import React from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Box, Stack, Text } from "@mantine/core";
import { FormTemplateField } from "../../../types/formTemplates";
import { SortableFieldCard } from "./SortableFieldCard";

interface FieldCanvasProps {
  fields: FormTemplateField[];
  errorsByFieldId: Record<string, string[]>;
  onUpdateField: (field: FormTemplateField) => void;
  onRemoveField: (id: string) => void;
  onMoveField: (id: string, direction: "up" | "down") => void;
}

export const FieldCanvas: React.FC<FieldCanvasProps> = ({
  fields,
  errorsByFieldId,
  onUpdateField,
  onRemoveField,
  onMoveField,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: "canvas" });

  const fieldIds = fields.map((f) => `field:${f.id}`);

  return (
    <Box
      ref={setNodeRef}
      style={{
        flex: 1,
        padding: 24,
        overflowY: "auto",
        background: isOver
          ? "var(--mantine-color-teal-0)"
          : "var(--mantine-color-gray-0)",
      }}
    >
      <Box
        style={{
          maxWidth: 800,
          margin: "0 auto",
          background: "var(--mantine-color-white)",
          borderRadius: 12,
          border: "1px solid var(--mantine-color-gray-2)",
          padding: 24,
          minHeight: 400,
        }}
      >
        {fields.length === 0 ? (
          <Box
            p="xl"
            style={{
              textAlign: "center",
              border: "2px dashed var(--mantine-color-gray-3)",
              borderRadius: 12,
              background: "var(--mantine-color-gray-0)",
            }}
          >
            <Text fw={600}>Build your form</Text>
            <Text c="dimmed" size="sm" mt={4}>
              Drag elements from the sidebar or click to add them.
            </Text>
          </Box>
        ) : (
          <SortableContext
            items={fieldIds}
            strategy={verticalListSortingStrategy}
          >
            <Stack gap="sm">
              {fields.map((field, index) => (
                <SortableFieldCard
                  key={field.id}
                  field={field}
                  index={index}
                  total={fields.length}
                  onUpdate={onUpdateField}
                  onRemove={() => onRemoveField(field.id)}
                  onMoveUp={() => onMoveField(field.id, "up")}
                  onMoveDown={() => onMoveField(field.id, "down")}
                  error={
                    errorsByFieldId[field.id]
                      ? errorsByFieldId[field.id].join("; ")
                      : null
                  }
                />
              ))}
            </Stack>
          </SortableContext>
        )}
      </Box>
    </Box>
  );
};
